import {
  CatalogueResponse,
  CompetitionsResponse,
  FixtureResponse,
  GroupedFixtureResponse,
  PinMarketBody,
  PinnedMarketsResponse,
  PopularEventsResponse,
  ScorecardApiResponse,
} from '@/types/marketsType';
import apiSlice from './apiSlice';

const sportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSportCompetitions: builder.query<CompetitionsResponse, string>({
      query: (sportName) => ({
        url: `/competitions?`,
        method: 'GET',
        params: { sport: sportName == 'Football' ? 'Soccer' : sportName },
      }),
      providesTags: ['Competitions'],
    }),
    getPopularEvents: builder.query<PopularEventsResponse, void>({
      query: () => ({
        url: 'events/popular',
        method: 'GET',
      }),
      providesTags: ['Popular'],
    }),

    getMatches: builder.query<
      FixtureResponse,
      { sportName?: string; inplay?: boolean; seriesId?: string; search?: string; status?: string }
    >({
      query: ({ inplay, sportName, seriesId, search, status }) => ({
        url: '/fixture',
        params: {
          // Legacy callers (e.g. Sidebar) pass lowercase 'cricket'/'tennis'/'soccer' —
          // keep proper-casing those. Any other sport name (already proper-cased by
          // its caller, e.g. Navbar's '/all-sports/:sportName' routes) passes through.
          sport: ['cricket', 'tennis', 'soccer'].includes(sportName ?? '')
            ? sportName!.charAt(0).toUpperCase() + sportName!.slice(1)
            : sportName,
          competitionId: seriesId,
          search,
          inplay,
          status,
        },
      }),
      providesTags: ['Sports'],
    }),
    getMatchesGroupedByCompetition: builder.query<
      GroupedFixtureResponse,
      { sportName?: string; sortBy?: 'time' | 'competition'; inplay?: boolean; search?: string }
    >({
      query: ({ sportName, sortBy = 'time', inplay, search }) => ({
        url: '/fixture/groupByCompetition',
        params: {
          sport: sportName === 'football' ? 'Soccer' : sportName,
          sortBy,
          ...(inplay === undefined ? {} : { inplay }),
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ['Group'],
    }),
    getMatcheDetails: builder.query<CatalogueResponse, { sportName: string; eventId: string }>({
      query: ({ sportName, eventId }) => {
        const url = `/catalogue/${eventId}`;
        return url;
      },
      providesTags: ['Catalogue'],
    }),

    getMediaUrls: builder.query<ScorecardApiResponse, string>({
      query: (eventId) => ({
        url: `events/scorecard/${eventId}`,
      }),
    }),

    getPinnedMarkets: builder.query<PinnedMarketsResponse, { page?: number; limit?: number }>({
      query: ({ page, limit } = {}) => ({
        url: '/pinned-market',
        method: 'GET',
        params: {
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ts: Date.now(),
        },
      }),
      providesTags: ['PinnedMarkets'],
    }),
    pinMarket: builder.mutation<any, PinMarketBody>({
      query: (body) => ({
        url: '/pinned-market',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PinnedMarkets'],
    }),
    unpinMarket: builder.mutation<any, { eventId: number | string; marketId: string }>({
      query: ({ eventId, marketId }) => ({
        url: `/pinned-market/${eventId}`,
        method: 'DELETE',
        params: { marketId },
      }),
      invalidatesTags: ['PinnedMarkets'],
    }),
  }),
});

export const {
  useGetSportCompetitionsQuery,
  useGetMatchesQuery,
  useLazyGetMatchesQuery,
  useGetMatchesGroupedByCompetitionQuery,
  useLazyGetMatchesGroupedByCompetitionQuery,
  useGetMatcheDetailsQuery,
  useGetMediaUrlsQuery,
  useGetPopularEventsQuery,
  useLazyGetSportCompetitionsQuery,
  useGetPinnedMarketsQuery,
  usePinMarketMutation,
  useUnpinMarketMutation,
} = sportsApi;
